import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isLandingValidationSkipEnabledSelector } from 'store/common/selectors';

export enum ValidationsRules {
  Required = 'requiredValidation',
  SelectAge = 'selectAgeValidation',
  Age = 'ageValidation',
  AgeRequired = 'ageRequiredValidation',
  Email = 'emailValidation',
  EmailRequired = 'emailRequiredValidation',
  Password = 'passwordValidation',
  PasswordRequired = 'passwordRequiredValidation',
  Username = 'usernameValidation',
  UsernameRequired = 'usernameRequiredValidation',
}

export const useValidationRules = () => {
  const { t } = useTranslation(['common', 'app']);

  const { pathname } = useLocation();

  const isLandingValidationSkipEnabled = useSelector(
    isLandingValidationSkipEnabledSelector
  );

  const isValidationTestEnabled = useMemo(
    () => isLandingValidationSkipEnabled && pathname.includes('landing'),
    [isLandingValidationSkipEnabled, pathname]
  );

  const requiredValidation = {
    required: {
      value: true,
      message: t('common:validations.requiredField'),
    },
  };

  const selectAgeValidation = {
    validate: (value: string) => {
      if (Number.isNaN(Number(value))) {
        return t('common:validations.requiredField');
      }
      return true;
    },
  };

  const ageValidation = {
    min: {
      value: 18,
      message: isValidationTestEnabled
        ? t('app:validations.ageMinTest')
        : t('common:validations.ageMin'),
    },
    max: {
      value: 100,
      message: isValidationTestEnabled
        ? t('app:validations.ageMaxTest')
        : t('common:validations.ageMax'),
    },
  };

  const ageRequiredValidation = {
    ...requiredValidation,
    ...ageValidation,
  };

  const emailValidation = {
    pattern: {
      value: /.*/,
      message: t('common:validations.email'),
    },
  };

  const emailRequiredValidation = {
    ...requiredValidation,
    ...emailValidation,
  };

  const passwordValidation = {
    minLength: {
      value: isValidationTestEnabled ? 6 : 8,
      message: isValidationTestEnabled
        ? t('app:validations.passwordMinTest')
        : t('common:validations.passwordMin'),
    },
    maxLength: {
      value: 40,
      message: t('common:validations.passwordMax'),
    },
    pattern: {
      value: isLandingValidationSkipEnabled ? /^[\S| ]*$/ : /^[\S]*$/,
      message: t('common:validations.passwordInvalid'),
    },
  };

  const passwordRequiredValidation = {
    ...requiredValidation,
    ...passwordValidation,
  };

  const usernameValidation = {
    maxLength: {
      value: 30,
      message: t('common:validations.usernameMax'),
    },
    minLength: {
      value: 2,
      message: t('common:validations.usernameMin'),
    },
    pattern: {
      value: isLandingValidationSkipEnabled
        ? /^[\x21-\x7E- ]*$/
        : /^[\x21-\x7E]*$/,
      message: t('common:validations.usernameMax'),
    },
  };

  const usernameRequiredValidation = {
    ...requiredValidation,
    ...usernameValidation,
  };

  return {
    [ValidationsRules.Required]: requiredValidation,
    [ValidationsRules.SelectAge]: selectAgeValidation,
    [ValidationsRules.Email]: emailValidation,
    [ValidationsRules.EmailRequired]: emailRequiredValidation,
    [ValidationsRules.Password]: passwordValidation,
    [ValidationsRules.PasswordRequired]: passwordRequiredValidation,
    [ValidationsRules.Age]: ageValidation,
    [ValidationsRules.AgeRequired]: ageRequiredValidation,
    [ValidationsRules.Username]: usernameValidation,
    [ValidationsRules.UsernameRequired]: usernameRequiredValidation,
  };
};
